import { writeBatch } from 'firebase/firestore';
import { ParentContext } from 'global/cnr/contexts/ParentContext';
import { _cacheGroupTypes } from 'global/cnr/contexts/useHelpers/useTimestamp';
import { dataFix } from 'global/common/dataFix';
import { createRefPath_event, createRefPath_events } from 'global/firestoreData/appData/appRefPaths';
import _ from 'lodash';
import { useCallback, useContext, useRef } from 'react';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { seasonalUpdateTypes } from '../cnr/reducers/reducerHelpers/seasonalDataHelpers';
import { seasonalHelpers } from '../cnr/reducers/reducerHelpers/seasonalHelpers';
import { useSeasonalData, useSeasonals } from './useSeasonals';

export const _allowUpdates_sports = true

export const useCreateSeasonData = () => {

  const parentContext = useContext(ParentContext)
  const { fns, states } = parentContext ?? {}
  const { appUser_state, app_state, paps_state } = states ?? {}
  const { mainDb } = app_state ?? {}
  const { appUser } = appUser_state ?? {}
  const { pathViews } = paps_state ?? {}
  const { database_fns } = fns

  const { sportsSeason_state } = SportsSeasonContext ?? {}
  const {
    currentSeason_info,
    links_score,
    matches_info,
    parentDataKeys,
    sportPermissions,
  } = sportsSeason_state ?? {}

  const { ammend_updateProps, ammend_updateNameProps } = useSeasonals()
  const { update_seasonalOrGlobal, getUpdateProps, getSports_collectionItem } = useSeasonalData()

  const datasToUpdateRef = useRef({
    seasonal: {},
    teams: {},
    matches: {}
  })

  const ammend_sportsSeasonalItem = useCallback((seasonal) => {
    datasToUpdateRef.current.seasonal = seasonal
    return seasonal
  }, [])

  const ammend_seasonalData = useCallback((payload) => {
    datasToUpdateRef.current.seasonal = payload
    return payload
  }, [])

  const ammend_sportsTeams = useCallback((teams) => {
    datasToUpdateRef.current.teams = teams
    return teams
  }, [])

  const ammend_sportsSeasonDataToUpdate = useCallback(
    ({ matches, seasonal, teams }) => {
      datasToUpdateRef.current = {
        matches,
        seasonal,
        teams
      }
      return datasToUpdateRef.current
    },
    []
  )

  const update_seasonSportsData = useCallback(
    ({ callback }) => {
      return updateSportsData(
        database_fns,
        mainDb,
        pathViews,
        datasToUpdateRef.current,
        '',
        appUser,
        callback
      )
    },
    []
  )

  const update_seasonalData = useCallback(async ({ opts, updateType }) => {
    const _opts = { ...opts, updateType, pathViews, appUser, sportPermissions }
    updateSeasonalUpdate({
      ammend_updateNameProps,
      ammend_updateProps,
      database_fns,
      getSports_collectionItem,
      getUpdateProps,
      mainDb,
      opts: _opts,
      update_seasonalOrGlobal,
    })
    return { updateSeasonalUpdate }
  }, []);


  const reset = useCallback(() => {
    datasToUpdateRef.current = {
      seasonal: {},
      teams: {},
      matches: {}
    }
  }, [])

  const createSeasonFromPrevious = useCallback((sportSeasonals, selectedYear, homeAways, callback) => {
    const _previousYear = selectedYear - 1
    const _previousSeason = sportSeasonals[_previousYear]
    if (_previousSeason) {
      const { teams, sectionCounts, details, playoffQualifyCounts, parentKeys, sectionDates } = _previousSeason ?? {}
      parentKeys.sportsYears = selectedYear

      let _sectionDates = {};

      if (sectionDates) {
        _sectionDates = ammendSectionDates(sectionDates, selectedYear)
      }

      if (details) {
        _.forEach(details, (detail, detailKey) => {
          if (detail && detailKey.toLowerCase().indexOf('date') > 0) {
            details[detailKey] = convertDateToNewYear(detail, selectedYear)
          }
        })
      }

      const _newSeason = { teams, sectionCounts, details, playoffQualifyCounts, parentKeys, sectionDates: _sectionDates, homeAways }

      sportSeasonals[selectedYear] = { ..._newSeason }
      callback(sportSeasonals, selectedYear)
    }
  }, [])

  return {
    ammend_seasonalData,
    ammend_sportsTeams,
    ammend_sportsSeasonalItem,
    ammend_sportsSeasonDataToUpdate,
    update_seasonSportsData,
    update_seasonalData,
    reset,
    createSeasonFromPrevious,
    datasToUpdate: datasToUpdateRef.current
  }
}

const ammendSectionDates = (sectionDates, selectedYear) => {
  const _sectionDates = {}
  _.forEach(sectionDates, (sds, sdsKey) => {
    const newDates = sds.map(d =>
      convertDateToNewYear(d, selectedYear)
    );
    _sectionDates[sdsKey] = newDates
  })
  return _sectionDates
}

const updateSportsData = async (
  database_fns,
  mainDb,
  pathViews,
  datasToUpdate,
  seasonalKey,
  appUser,
  callback,
) => {
  const { displayName, uid } = appUser ?? {}
  const _timestamp = getNow()

  const status = {
    uid,
    displayName,
    appVersion: _appVersion,
    lastUpdate: _timestamp
  }

  const allows = {
    matches_seasonal: !_.isEmpty(datasToUpdate?.matches_seasonal),
    seasonals: !_.isEmpty(datasToUpdate?.seasonals),
    teams_seasonal: !_.isEmpty(datasToUpdate?.teams_seasonal)
  }

  const paths = {
    matches_seasonal: allows.matches_seasonal && createRefPath_events(pathViews, ['_matches_seasonal']),
    seasonals: allows.seasonals && createRefPath_events(pathViews, ['_seasonals']),
    teams_seasonal: allows.views && createRefPath_events(pathViews, ['_teams_seasonal'])
  };

  const batch = writeBatch(mainDb);

  // (refPath, data, callback, batch, appUserAccess, storagePaths, vit) => fs_update_doc(mainDb, refPath, data, callback, batch, appUserAccess, storagePaths, vit)
  if (allows.matches_seasonal && paths.matches_seasonal) {
    database_fns.update_doc(paths.matches_seasonal, datasToUpdate.matches_seasonal, null, batch);
  }
  if (allows.seasonals && paths.seasonals) {
    database_fns.update_doc(paths.seasonals, datasToUpdate.seasonals, null, batch);
  }
  if (allows.views && paths.views) {
    database_fns.update_doc(paths.views, datasToUpdate.teams_seasonal, null, batch);
  }

  console.group('updateSettings_updatesOnly', baseRef)
  console.log('datasToUpdate', datasToUpdate)
  console.log('paths', paths)
  console.log('status', status)

  _.forEach(batch?._mutations, (m) => {
    if (m?.key?.path?.segments) {
      console.log('segments', m?.key?.path?.segments)
    }
  })

  console.groupEnd()

  if (_allowUpdates_sports) {
    database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonInfo, seasonalKey, _timestamp, batch)
    batch.commit();
  }

  if (callback) {
    callback();
  }

}

const ammendMatches = (matches) => {
  const _matches = _.groupBy(matches, '_matchCollectionKey')
  console.log('_matches', _matches)
}

const groupMatchesByCollectionKey = (matches) =>
  _.transform(
    matches,
    (acc, match, matchKey) => {
      const groupKey = match._matchCollectionKey;
      if (!groupKey) return;

      if (!acc[groupKey]) acc[groupKey] = {};
      acc[groupKey][matchKey] = match;
    },
    {}
  );


const updateSeasonalUpdate = async ({
  opts,
  getSports_collectionItem,
  database_fns,
  mainDb,
}) => {

  const {
    pathViews,
    selectedYear,
    sportsSeason_selected,
    sportsKey,
    _sportsAbr,
    callback,
    updateType
  } = opts ?? {}

  const {
    seasonals: {
      details,
      playoffQualifyCounts,
      matches,
      sectionDates,
      homeAways,
      teams,
    },
  } = sportsSeason_selected

  let sportCollectionName = ''

  switch (updateType) {
    case seasonalUpdateTypes.fullSeason_teams:
      sportCollectionName = '_teams_seasonal'
      break;
    case seasonalUpdateTypes.fullSeason_matches:
      sportCollectionName = '_matches_seasonal'
      break;
    default:
      sportCollectionName = '_seasonals'
  }

  const _sportsDocPrefix = `${_sportsAbr}_${selectedYear}`
  const _pks = seasonalHelpers.getPks(pathViews, selectedYear, sportsKey)
  const _sportsYearCollectionItem = await getSports_collectionItem(_pks, sportCollectionName, true)

  let _docKey;
  let _isNew = false;

  if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {
    const { _itemKey: sycKey } = _sportsYearCollectionItem
    _docKey = sycKey
  } else {
    _docKey = _sportsDocPrefix + '_' + database_fns.get_docKey()
    _isNew = true
  }

  let dataToUpdate;

  switch (updateType) {
    case seasonalUpdateTypes.fullSeason_teams:
      dataToUpdate = { teams: teams }
      break;

    case seasonalUpdateTypes.fullSeason_matches:
      dataToUpdate = { matches: matches }
      break;

    case seasonalUpdateTypes.details:
      dataToUpdate = { details: details }
      if (playoffQualifyCounts) {
        dataToUpdate.playoffQualifyCounts = playoffQualifyCounts
      }
      break;
    case seasonalUpdateTypes.teams:
      dataToUpdate = { teams: teams }
      break;

    case seasonalUpdateTypes.sectionCrossovers:
      break;

    case seasonalUpdateTypes.sectionDates:
      dataToUpdate = { sectionDates: sectionDates }
      break;

    case seasonalUpdateTypes.matches:
      dataToUpdate = {
        matches: matches
      }
      if (homeAways) {
        dataToUpdate.homeAways = homeAways
      }
      break;
    default:
    // nothing
  }

  if (_isNew && dataToUpdate) {
    dataToUpdate.parentKeys = { ..._pks }
  }

  if (!dataToUpdate) {
    callback()
    return;
  }

  dataToUpdate = dataFix.removeNulls(dataToUpdate)

  const batch = writeBatch(mainDb);

  const refPath = createRefPath_event(pathViews, [sportCollectionName, _docKey])

  const paths = []

  if (refPath) {

    switch (updateType) {
      case seasonalUpdateTypes.fullSeason_matches:
      case seasonalUpdateTypes.fullSeason_teams:
        break;
      default:
        if (!_.isEmpty(dataToUpdate)) {
          if (_isNew) {
            database_fns.set_doc(refPath, dataToUpdate, true, false, batch);
          } else {
            database_fns.update_doc(refPath, dataToUpdate, null, batch);
          }
          //update_doc: (refPath, data, callback, batch, appUserAccess, storagePaths, vit)
          paths.push({
            path: refPath,
            data: dataToUpdate
          })
        }
    }

    // subCollections
    switch (updateType) {
      case seasonalUpdateTypes.fullSeason_matches:
        if (matches) {
          const groupedMatches = groupMatchesByCollectionKey(matches)
          // each key is a document
          _.forEach(groupedMatches, (groupMatches, groupKey) => {
            const _subPath = createRefPath_event(pathViews, [sportCollectionName, _docKey, 'matches', groupKey]);
            paths.push({
              path: _subPath,
              matches: groupMatches
            })
            database_fns.set_doc(
              _subPath,
              { matches: groupMatches }, // only this group's matches
              false, // DO NOT MERGE, this is an overwrite
              false,
              batch
            );
          });
          console.log('groupedMatches', paths)
        }
        break;
      default:
      // nothing 
    }
  }

  console.group('updateSeasonalUpdate', updateType)
  console.log('paths', paths)
  console.groupEnd()

  if (_allowUpdates_sports) {
    batch.commit();
  }

  callback()

}


const xxxx = () => {
  return {
    _matches_seasonal: {
      docKey: {
        parentKeys: {
          districts: 'districtsKey',
          organizations: 'organizationsKey',
          sports: 'sportsKey',
          sportsYears: 'sportsYears',
        },
        matches: {
          ['1A']: {
            matches: {}
          }
        },
        playoffMatches: {}
      }
    },
    _seasonals: {
      ['_seasonalsKey']: {
        parentKeys: {
          districts: 'districtsKey',
          organizations: 'organizationsKey',
          sports: 'sportsKey',
          sportsYears: 'sportsYears',
        },
        details: {},
        playoffQualifyCounts: {},
        sectionCounts: {},
        sectionDates: {},
        teams: {}
      },
      _teams_seasonal: {
        ['_teams_seasonalKey']: {
          parentKeys: {
            districts: 'districtsKey',
            organizations: 'organizationsKey',
            sports: 'sportsKey',
            sportsYears: 'sportsYears',
          },
          teams: {}
        }
      }

    }
  }

}

const parseDate = (str) => {
  const [month, day, year] = str.split('/').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date) => {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const getNthWeekdayOfMonth = ({ year, month, weekday, nth }) => {
  let count = 0;

  for (let day = 1; day <= 31; day++) {
    const date = new Date(year, month, day);
    if (date.getMonth() !== month) break;

    if (date.getDay() === weekday) {
      count++;
      if (count === nth) return date;
    }
  }

  // fallback → last occurrence if nth doesn't exist
  let lastMatch = null;
  for (let day = 1; day <= 31; day++) {
    const date = new Date(year, month, day);
    if (date.getMonth() !== month) break;

    if (date.getDay() === weekday) {
      lastMatch = date;
    }
  }

  return lastMatch;
};

const convertDateToNewYear = (dateStr, newYear) => {
  const d = parseDate(dateStr);

  const month = d.getMonth();
  const weekday = d.getDay();

  // determine nth occurrence
  let nth = 0;
  for (let day = 1; day <= d.getDate(); day++) {
    const temp = new Date(d.getFullYear(), month, day);
    if (temp.getDay() === weekday) nth++;
  }

  const newDate = getNthWeekdayOfMonth({
    year: newYear,
    month,
    weekday,
    nth
  });

  return formatDate(newDate);
};